import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import { localStorageEnum } from '../../api/enums';
import SideSheet from '../../components/NewSideSheet';
import ThreeUsers from '../../assets/icons/three-users.svg';
import WithdrawalManager from '../../assets/icons/withdrawal-manager.svg';
import USDRequests from '../../assets/icons/USD-Requests.svg';
import ReceiptImg from '../../assets/icons/receipt.svg';
import SettingsIcon from '../../assets/icons/settings.svg';
import SiteConfig from '../../assets/icons/site-configuration.svg';
import PaymentProcessor from '../../assets/icons/payment-prcoessor.svg';
import { PiArrowBendDownRightBold } from 'react-icons/pi';
import './Home.scss';
import { QuickLink } from '../../types';

const baseLinks: QuickLink[] = [
  {
    title: 'Manage Users',
    description: 'Manage user profiles, transactions, products, and account activities in one place.',
    icon: ThreeUsers,
    path: '/customers',
  },
  {
    title: 'Withdrawal Manager',
    description: 'Review and process pending withdrawal requests.',
    icon: WithdrawalManager,
    path: '/withdrawal-manager',
  },
  {
    title: 'USD Requests',
    description: 'Manage USD conversion requests and approvals.',
    icon: USDRequests,
    path: '/usd-request',
  },
  {
    title: 'Settings',
    description: 'Manage your account, team roles, and permissions.',
    icon: SettingsIcon,
    path: '/settings',
  },
];

const adminOnlyLinks: QuickLink[] = [
  {
    title: 'Repush Webhook',
    description: 'Re-trigger webhook notifications for failed or pending events.',
    icon: ReceiptImg,
    path: '/repush-webhook',
  },
  {
    title: 'Site Configuration',
    description: 'Manage site-wide settings and feature toggles.',
    icon: SiteConfig,
    path: '/site-configuration',
  },
  {
    title: 'Payment Processor',
    description: 'View and manage payment processor configurations.',
    icon: PaymentProcessor,
    path: '/payment-processor',
  },
];

const complianceOnlyLinks: QuickLink[] = [
  {
    title: 'Repush Webhook',
    description: 'Re-trigger webhook notifications for failed or pending events.',
    icon: ReceiptImg,
    path: '/repush-webhook',
  },
];

const adminUserIds = ['82c2277ef71119', '7d0ca1bde4', '4ba9235b720220'];
const adminComplianceUserIds = ['36439545a01219', '6764d09d7e5'];

const changelogItems = [
  {
    date: 'April 2026',
    items: [
      'Overview dashboard — quick-access cards for every section',
      'Transaction status checker — look up any transaction instantly',
    ],
  },
  {
    date: 'March 2026',
    items: [
      'Large Withdrawals view — separate queue for high-value requests',
      'Held USD Transactions — manage withheld dollar conversions',
      'Quick Actions on Flex Naira — pay interest by month or day',
    ],
  },
  {
    date: 'February 2026',
    items: [
      'Roles & Permissions — granular access control for admin users',
      'Bulk Operations — upload CSV for batch processing',
      'Improved customer search — faster lookups by ID, email, or name',
    ],
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [showChangelog, setShowChangelog] = useState(false);

  useEffect(() => {
    setFirstName(localStorage.getItem(localStorageEnum['local.storage.firstName']) ?? '');
  }, []);

  const quickLinks = useMemo(() => {
    const userId = localStorage.getItem(localStorageEnum['local.storage.userId']) ?? '';
    const isAdmin = adminUserIds.includes(userId);
    const isCompliance = adminComplianceUserIds.includes(userId);

    const links = [...baseLinks];
    if (isAdmin) links.push(...adminOnlyLinks);
    else if (isCompliance) links.push(...complianceOnlyLinks);
    return links;
  }, []);

  return (
    <>
      <Header name="Overview" />
      <div className="home-page">
        <div className="home-page__container">
          <section className="home-page__greeting">
            <h1>Hi {firstName || 'there'},</h1>
            <p>Welcome to your dashboard. How are you doing today?</p>
          </section>

          <div className="home-page__layout">
            <div className="home-page__main">
              <div className="home-page__grid">
                {quickLinks.slice(0, 5).map((link) => (
                  <div key={link.path} className="home-page__card" onClick={() => navigate(link.path)}>
                    <div className="home-page__card-icon">
                      <img src={link.icon} alt="" />
                    </div>
                    <h3 className="home-page__card-title">{link.title}</h3>
                    <p className="home-page__card-desc">{link.description}</p>
                    <span className="home-page__card-link">
                      <PiArrowBendDownRightBold size={14} /> Visit
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <aside className="home-page__aside">
              <div className="home-page__whats-new">
                <div className="home-page__whats-new-badge">New</div>
                <h3>What&apos;s new on the Admin dashboard</h3>
                <p>We just launched a feature to help you check the status of any transaction.</p>
                <button className="home-page__whats-new-btn" onClick={() => setShowChangelog(true)}>
                  See what&apos;s new
                </button>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <SideSheet
        title="What's New"
        subTitle="Recent updates and improvements to the admin dashboard."
        active={showChangelog}
        onClose={() => setShowChangelog(false)}
      >
        <div className="changelog">
          {changelogItems.map((group) => (
            <div key={group.date} className="changelog__group">
              <h4 className="changelog__date">{group.date}</h4>
              <ul className="changelog__list">
                {group.items.map((item, i) => (
                  <li key={i} className="changelog__item flex--2">
                    {/* <span className="changelog__dot" /> */}
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </SideSheet>
    </>
  );
};

export default Home;
